/**
 * Schema.org Structured Data Generator
 * Reusable functions for creating JSON-LD structured data
 */

// Use environment variable for site URL, fallback to window.location.origin for runtime
const getSiteUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || 'https://piercepremiercleaning.com';
};

const siteUrl = getSiteUrl();

/**
 * Generate LocalBusiness schema
 */
export const generateLocalBusinessSchema = (options = {}) => {
  return {
    "@type": "LocalBusiness",
    "@id": `${siteUrl}/#business`,
    "name": "Pierce Premier Cleaning Services",
    "image": "https://customer-assets-7cd3h4nn.emergentagent.net/job_pierce-premier/artifacts/dk5ckiun_image.png",
    "telephone": "+1-321-408-1347",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Palm Bay",
      "addressRegion": "FL",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "28.0345",
        "longitude": "-80.5887"
      },
      "name": "Brevard County, Florida"
    },
    "priceRange": "$$",
    "openingHours": ["Mo-Fr 08:00-18:00", "Sa 09:00-16:00"],
    ...options
  };
};

/**
 * Generate Service schema
 */
export const generateServiceSchema = ({
  serviceName,
  description,
  areaServed = "Brevard County, Florida"
}) => {
  return {
    "@type": "Service",
    "serviceType": serviceName,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Pierce Premier Cleaning Services",
      "telephone": "+1-321-408-1347"
    },
    "description": description,
    "areaServed": {
      "@type": "City",
      "name": areaServed
    }
  };
};

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  return {
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      ...(crumb.url && { "item": `${siteUrl}${crumb.url}` })
    }))
  };
};

/**
 * Generate FAQPage schema
 */
export const generateFAQSchema = (faqs) => {
  return {
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate WebPage schema
 */
export const generateWebPageSchema = ({
  name,
  description,
  url
}) => {
  return {
    "@type": "WebPage",
    "name": name,
    "description": description,
    "url": `${siteUrl}${url}`,
    "breadcrumb": {
      "@id": `${siteUrl}${url}#breadcrumb`
    }
  };
};

/**
 * Generate AggregateRating schema
 */
export const generateAggregateRatingSchema = ({
  ratingValue = "5",
  reviewCount = "50",
  bestRating = "5"
} = {}) => {
  return {
    "@type": "AggregateRating",
    "ratingValue": ratingValue,
    "reviewCount": reviewCount,
    "bestRating": bestRating
  };
};

/**
 * Generate Organization schema (complements LocalBusiness)
 * Use on key pages like homepage, about, why-choose-us
 */
export const generateOrganizationSchema = (options = {}) => {
  return {
    "@type": "Organization",
    "@id": `${siteUrl}/#organization`,
    "name": "Pierce Premier Cleaning Services",
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets-7cd3h4nn.emergentagent.net/job_pierce-premier/artifacts/dk5ckiun_image.png",
      "width": "400",
      "height": "150"
    },
    "image": "https://customer-assets-7cd3h4nn.emergentagent.net/job_pierce-premier/artifacts/dk5ckiun_image.png",
    "description": "Pierce Premier is a locally-owned, husband-and-wife cleaning business serving Brevard County, Florida. Specializing in residential and commercial cleaning with over 15 years of experience.",
    "founder": [
      {
        "@type": "Person",
        "name": "Bob"
      },
      {
        "@type": "Person",
        "name": "Heather"
      }
    ],
    "telephone": "+1-321-408-1347",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Palm Bay",
      "addressRegion": "FL",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "State",
      "name": "Florida"
    },
    "sameAs": [
      // Add social media profiles when available
      // "https://www.facebook.com/piercepremiercleaning",
      // "https://www.instagram.com/piercepremiercleaning"
    ],
    ...options
  };
};

/**
 * Combine multiple schema objects into a @graph
 */
export const combineSchemas = (...schemas) => {
  return {
    "@context": "https://schema.org",
    "@graph": schemas
  };
};
